/* =============================================================================
   VIEWS / CUSTOMER-STATUS — Live-Tracking für anonyme Gäste (REF-7c, S76)
   =============================================================================
   Behandelt `#track=<token>` im URL-Hash. Das Token ist `public_status_token`
   aus orders (Präfix "s"), gesetzt per BEFORE-INSERT-Trigger (phase_76). Eine
   read-only SECURITY-DEFINER-RPC (`get_order_public_status_by_token`) liefert
   den Lifecycle ohne PII außer dem Kund-eigenen Namen.

   ANON-REALTIME-HINWEIS: Anonyme Clients haben keinen RLS-SELECT auf orders,
   ein direkter postgres_changes-Subscribe liefert daher keine Daten. Deshalb
   ist das Tracking POLLING-basiert (Default 20s) — robust und RLS-konform.
   Sobald die Bestellung in einem Endzustand ist, stoppt das Polling.

   Anders als der Storno-Flow gibt es hier KEINE Mutation — reiner Lese-Pfad,
   prefetch-unkritisch.
============================================================================ */

import { htmlEscape, fmtDate, fmtEUR } from '../utils/helpers.js';
// S234 T1 — Bezahl-Link auf der Status-Seite, solange die Order (auf DIESEM Gerät)
// als offen gemerkt ist (löst das One-Shot-Problem des Erfolgsdialogs).
import { getPendingStripeOrderByToken } from '../core/stripe-checkout.js';

const POLL_INTERVAL_MS = 20000;
let _pollTimer = null;
let _pollToken = null;

function getState() {
  return (window.App && window.App.storage && window.App.storage._getInternalState())
         || { settings: {} };
}

/** Wenn `#track=<token>` in der URL steht: View wechseln + Status laden +
 *  Polling starten. Rein lesend. */
export function checkTrackingRequestFromUrl() {
  const token = _parseTrackTokenFromHash(window.location.hash);
  if (!token) return false;
  renderTrackingLoading();
  if (typeof window.switchView === 'function') window.switchView('tracking');
  _pollToken = token;
  loadAndRenderTracking(token);
  _startTrackingPoll(token);
  // S77-B: In-Page-Hashwechsel (#track=A → #track=B) ohne Reload abfangen.
  // bootstrap.js dispatcht checkTrackingRequestFromUrl() nur einmal beim Init;
  // ohne diesen Listener bliebe beim Token-Wechsel der alte Order-State stehen.
  _bindTrackingHashListener();
  return true;
}

/** S82-2: Pure Skeleton-HTML für den Tracking-Loading-State.
 *  Wird sofort gezeigt (auch vor dem ersten RPC), damit der Gast nach Klick
 *  auf den Tracking-Link KEINE weiße Seite sieht. Pure → Vitest-testbar. */
export function buildTrackingSkeletonHtml() {
  return `
    <h2>Bestellung wird geladen <span class="tracking-skeleton-dots" aria-hidden="true">…</span></h2>
    <p style="color:var(--ink-soft);">Einen Moment bitte, wir holen den aktuellen Status.</p>
    <div class="tracking-skeleton" aria-hidden="true" style="margin-top:14px;">
      <div class="tracking-skeleton-bar" style="width:60%;height:18px;border-radius:6px;background:var(--bg-soft,#eee);margin:10px 0;"></div>
      <div class="tracking-skeleton-bar" style="width:85%;height:14px;border-radius:6px;background:var(--bg-soft,#eee);margin:10px 0;"></div>
      <div class="tracking-skeleton-bar" style="width:45%;height:14px;border-radius:6px;background:var(--bg-soft,#eee);margin:10px 0;"></div>
    </div>`;
}

/** Loading-State, während die erste RPC läuft. */
export function renderTrackingLoading() {
  const wrap = document.getElementById('tracking-card');
  if (!wrap) return;
  wrap.innerHTML = buildTrackingSkeletonHtml();
}

/** Einmal laden + rendern. Bei Endzustand Polling stoppen. */
export async function loadAndRenderTracking(token) {
  try {
    const order = await window.App.storage.orders.getPublicStatusByToken(token);
    renderTrackingView(order);
    const stage = order ? _deriveTrackingStage(order) : null;
    if (!order || (stage && stage.terminal)) _stopTrackingPoll();
  } catch (e) {
    console.error('[loadAndRenderTracking]', e);
    renderTrackingView(null);
    _stopTrackingPoll();
  }
}

/** Tracking-Card rendern: Fortschritts-Stufen + Eckdaten. */
export function renderTrackingView(order) {
  const state = getState();
  const wrap = document.getElementById('tracking-card');
  if (!wrap) return;

  if (!order) {
    wrap.innerHTML = `
      <h2>Bestellung nicht gefunden</h2>
      <p>Der Tracking-Link ist ungültig oder die Bestellung wurde entfernt.</p>
      <p style="color:var(--ink-soft);">Bei Fragen ruf uns gern an:<br>
        Telefon: ${htmlEscape(state.settings.restaurantPhone || '—')}</p>
      <a class="btn btn-secondary" href="#" onclick="closeTrackingView();return false;">Zur Startseite</a>`;
    return;
  }

  const stage = _deriveTrackingStage(order);
  const stepsHtml = stage.steps.map((s, i) => {
    const isDone = i < stage.activeIndex;
    const isActive = i === stage.activeIndex;
    const dot = isDone ? '✓' : (isActive ? '●' : '○');
    const color = isDone ? 'var(--ok, #2e7d32)'
                : isActive ? 'var(--accent, #c8102e)'
                : 'var(--ink-soft, #999)';
    const weight = isActive ? '700' : '400';
    return `<div style="display:flex;align-items:center;gap:10px;padding:6px 0;color:${color};font-weight:${weight};">
        <span style="font-size:1.1rem;width:1.2em;text-align:center;">${dot}</span>
        <span>${htmlEscape(s.label)}</span>
      </div>`;
  }).join('');

  const whenLabel = order.serviceType === 'delivery' ? 'Lieferung' : 'Abholung';
  const total = fmtEUR((Number(order.totalGrossCents) || 0) / 100);

  // S234 T1 — Bezahl-Insel: nur wenn die Order auf DIESEM Gerät als offen gemerkt
  // ist (Pending-Registry, per Tracking-Token) und die Bestellung nicht in einem
  // Endzustand/storniert ist. Der Edge-Call ist idempotent (bezahlt → kein
  // Doppel-Charge), daher ist ein evtl. verspäteter Button harmlos.
  const pendingStripe = (!stage.terminal && !stage.cancelled)
    ? getPendingStripeOrderByToken(_pollToken) : null;
  const stripeBlock = pendingStripe ? `
    <div style="margin:14px 0; padding:14px; border-radius:10px; background:#f0f7ff; border:1px solid #b8d4f5; color:#16221c;">
      <div style="font-weight:600; margin-bottom:6px;">💳 Noch nicht bezahlt?</div>
      <div style="font-size:0.92rem; color:#4a5a6e; margin-bottom:12px;">
        Du kannst deine Bestellung jetzt sicher online via Stripe begleichen — bei
        Abholung/Lieferung ist dann nichts mehr offen.
      </div>
      <button type="button" class="btn btn-primary" id="track-stripe-btn"
              onclick="startStripeCheckout('${String(pendingStripe.orderId).replace(/'/g, "\\'")}', 'track-stripe-btn')">
        Jetzt online bezahlen
      </button>
    </div>` : '';

  wrap.innerHTML = `
    <h2>${htmlEscape(stage.headline)}</h2>
    <p style="color:var(--ink-soft);">${htmlEscape(stage.subline)}</p>
    <div style="background:var(--bg-soft); padding:14px; border-radius:8px; margin:12px 0;">
      <div><strong>Bestellung Nr. ${htmlEscape(order.orderNumber || '—')}</strong></div>
      <div>${htmlEscape(order.customerName || '')}</div>
      <div>${whenLabel}: ${fmtDate(order.pickupDate)}${order.pickupTime ? ', ' + htmlEscape(order.pickupTime) + ' Uhr' : ''}</div>
      <div>Gesamt: ${total}</div>
    </div>
    <div style="margin:16px 0;">${stepsHtml}</div>
    ${stripeBlock}
    <a class="btn btn-secondary" href="#" onclick="closeTrackingView();return false;">Zur Startseite</a>`;
}

/** Tracking-View schließen, Polling stoppen, zurück zum Customer-Form. */
export function closeTrackingView() {
  _stopTrackingPoll();
  // S77-B: Token vergessen, damit ein späterer Rück-Navigations-Hash (Browser-
  // Back zu #track=<token>) als „neu" erkannt wird und re-fetcht.
  _pollToken = null;
  history.replaceState(null, '', window.location.pathname + window.location.search);
  if (typeof window.switchView === 'function') window.switchView('customer');
}

function _startTrackingPoll(token) {
  _stopTrackingPoll();
  _pollTimer = setInterval(() => {
    // Hash kann sich geändert haben (User navigiert weg) → dann stoppen.
    if (_parseTrackTokenFromHash(window.location.hash) !== token) {
      _stopTrackingPoll();
      return;
    }
    loadAndRenderTracking(token);
  }, POLL_INTERVAL_MS);
}

function _stopTrackingPoll() {
  if (_pollTimer) { clearInterval(_pollTimer); _pollTimer = null; }
}

// S77-B: hashchange-Listener für In-Page-Token-Wechsel. Nur einmal binden.
let _hashListenerBound = false;

function _bindTrackingHashListener() {
  if (_hashListenerBound) return;
  window.addEventListener('hashchange', _onTrackingHashChange);
  _hashListenerBound = true;
}

function _onTrackingHashChange() {
  const decision = _trackingHashAction(window.location.hash, _pollToken);
  if (decision.action !== 'reload') return;
  _stopTrackingPoll();
  _pollToken = decision.token;
  renderTrackingLoading();
  if (typeof window.switchView === 'function') window.switchView('tracking');
  loadAndRenderTracking(decision.token);
  _startTrackingPoll(decision.token);
}

// ---------------------------------------------------------------
// Pure helpers (Test-Coverage Schicht A)
// ---------------------------------------------------------------

/**
 * Parsed `#track=<token>` aus dem URL-Hash. Defensiv gegen URL-Encoding,
 * leeren Hash und malformierte Inputs.
 * @param {string} hash window.location.hash
 * @returns {string|null}
 */
function _parseTrackTokenFromHash(hash) {
  if (typeof hash !== 'string' || hash.length === 0) return null;
  const m = hash.match(/^#track=(.+)$/);
  if (!m) return null;
  try {
    const decoded = decodeURIComponent(m[1]);
    if (!decoded || decoded.length === 0) return null;
    return decoded;
  } catch (_) {
    return null;
  }
}

/**
 * S77-B: Pure-Decision für hashchange im Tracking-Kontext.
 * Entscheidet, ob der neue Hash ein Re-Fetch auslösen soll.
 *   - neues `#track=<token>` ≠ aktuelles Token → `{action:'reload', token}`
 *   - gleiches Token / kein Track-Hash         → `{action:'noop'}`
 * @param {string} newHash window.location.hash nach dem Wechsel
 * @param {string|null} currentToken aktuell getracktes Token (`_pollToken`)
 * @returns {{action:'reload', token:string}|{action:'noop'}}
 */
function _trackingHashAction(newHash, currentToken) {
  const token = _parseTrackTokenFromHash(newHash);
  if (token && token !== currentToken) return { action: 'reload', token };
  return { action: 'noop' };
}

/**
 * Leitet aus dem Order-Lifecycle die kundenfreundliche Tracking-Stufe ab.
 * Service-Type-abhängig: Delivery hat eine „Unterwegs"-Stufe, Pickup/Dine-In
 * eine „Abholbereit"-Stufe.
 *
 * @param {{status?:string, kitchenStatus?:string, deliveryStatus?:string, serviceType?:string}} order
 * @returns {{steps:Array<{key:string,label:string}>, activeIndex:number, terminal:boolean, cancelled:boolean, headline:string, subline:string}}
 */
function _deriveTrackingStage(order) {
  const o = order || {};
  const status = o.status || '';
  const kitchen = o.kitchenStatus || '';
  const delivery = o.deliveryStatus || '';
  const isDelivery = o.serviceType === 'delivery';

  const steps = isDelivery
    ? [
        { key: 'received',  label: 'Bestätigt' },
        { key: 'preparing', label: 'In Zubereitung' },
        { key: 'on_the_way', label: 'Unterwegs' },
        { key: 'delivered', label: 'Geliefert' },
      ]
    : [
        { key: 'received',  label: 'Bestätigt' },
        { key: 'preparing', label: 'In Zubereitung' },
        { key: 'ready',     label: 'Abholbereit' },
        { key: 'picked_up', label: 'Abgeholt' },
      ];

  // Endzustände zuerst.
  if (status === 'cancelled') {
    return {
      steps, activeIndex: -1, terminal: true, cancelled: true,
      headline: 'Bestellung storniert',
      subline: 'Diese Bestellung wurde storniert.',
    };
  }
  if (status === 'no_show') {
    return {
      steps, activeIndex: -1, terminal: true, cancelled: false,
      headline: 'Bestellung abgeschlossen',
      subline: 'Diese Bestellung wurde als nicht abgeholt vermerkt.',
    };
  }

  let activeIndex = 0;
  let terminal = false;

  if (isDelivery) {
    if (delivery === 'delivered') { activeIndex = 3; terminal = true; }
    else if (delivery === 'out_for_delivery') { activeIndex = 2; }
    else if (kitchen === 'preparing' || kitchen === 'ready' || delivery === 'assigned') { activeIndex = 1; }
    else { activeIndex = 0; }
  } else {
    if (status === 'picked_up') { activeIndex = 3; terminal = true; }
    else if (status === 'ready' || kitchen === 'ready') { activeIndex = 2; }
    else if (kitchen === 'preparing') { activeIndex = 1; }
    else { activeIndex = 0; }
  }

  const headline = terminal
    ? (isDelivery ? 'Bestellung geliefert' : 'Bestellung abgeholt')
    : 'Deine Bestellung';
  const sublines = isDelivery
    ? [
        'Wir haben deine Bestellung erhalten und bestätigt.',
        'Deine Bestellung wird gerade frisch zubereitet.',
        'Deine Bestellung ist unterwegs zu dir.',
        'Deine Bestellung wurde geliefert. Guten Appetit!',
      ]
    : [
        'Wir haben deine Bestellung erhalten und bestätigt.',
        'Deine Bestellung wird gerade frisch zubereitet.',
        'Deine Bestellung ist abholbereit!',
        'Deine Bestellung wurde abgeholt. Guten Appetit!',
      ];

  return {
    steps, activeIndex, terminal, cancelled: false,
    headline, subline: sublines[activeIndex] || sublines[0],
  };
}

// Backward-Compat / inline-Handler.
if (typeof window !== 'undefined') {
  Object.assign(window, {
    checkTrackingRequestFromUrl,
    renderTrackingLoading,
    renderTrackingView,
    closeTrackingView,
  });
}

// Test-Hook-Exports (zur Laufzeit No-Op).
export {
  _parseTrackTokenFromHash,
  _trackingHashAction,
  _deriveTrackingStage,
};
